import { useState } from "react";
import styled from "styled-components";

/*
 * Email and share, for a report somebody is looking at.
 *
 * WHY mailto AND NOT A SERVER SEND. The obvious build is a box for an email
 * address and a Lambda that sends the report to it. We are not doing that,
 * and the reason is in CLAUDE.md section 6g: the contact-form Lambda was an
 * OPEN RELAY until 19 October 2026 - the browser composed the whole message
 * and the function sent it as given, so anyone could send anything to anyone
 * from a verified sender of ours, and night-time bot bursts found it. The
 * fix was to accept four fields, validate them, and send to a FIXED
 * recipient from a FIXED sender.
 *
 * "Email this report to any address the visitor types" is a request to undo
 * exactly that. So the mail is composed in the visitor's own mail client:
 * nothing is posted anywhere, no address is collected, nothing of ours can
 * be used to send anything to anybody, and the privacy policy needs no new
 * paragraph because no personal data changes hands.
 *
 * WHAT THE LINK CARRIES. Postcode and certificate number, which is what the
 * page needs to rebuild the report - see the arrival effect in
 * PropertyReport - and, when there is one, a token naming a stored
 * valuation.
 *
 * THAT LAST PART IS NEW, AND THIS COMMENT USED TO SAY THE OPPOSITE. It said
 * the link could not carry the valuation because the figure was "generated
 * on request and never stored", and called the recipient asking for their
 * own "the honest behaviour anyway". That was a description of a defect
 * dressed as a principle: the sender had already paid a model for an answer,
 * and sharing the report threw it away. Valuations are stored now
 * (api/models/Valuation.js) and the link names one.
 *
 * A TOKEN, NOT THE FIGURE. A number in a URL is a number anyone can edit
 * before forwarding it, and this one is about somebody's home.
 */

const Row = styled.span`
    display: inline-flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

/*
 * The send panel sits under the buttons rather than in a modal: on a phone
 * this whole header is already narrow, and a dialog over a report the
 * reader is halfway through is worse than a row that pushes down.
 */
const Panel = styled.div`
    width: 100%;
    margin-top: 0.75rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;

    input {
        flex: 1 1 15rem;
        min-width: 0;
        padding: 0.8rem 0.9rem;
        font-size: 0.95rem;
        font-family: inherit;
        color: #fff;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.45);
        border-radius: ${(props) => props.theme.radius || "4px"};
        &::placeholder {
            color: rgba(255, 255, 255, 0.6);
        }
        &:focus {
            outline: 2px solid #fff;
            outline-offset: 1px;
        }
    }
`;

const Note = styled.p`
    width: 100%;
    margin: 0.4rem 0 0;
    font-size: 0.85rem;
    line-height: 1.4;
    color: ${(props) => (props.$bad ? "#ffd7d7" : "rgba(255,255,255,0.75)")};
`;

const Secondary = styled.button`
    padding: 0.85rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #fff;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: ${(props) => props.theme.radius || "4px"};
    cursor: pointer;
    transition: all 0.14s ease;
    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.14);
        border-color: #fff;
    }
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;

/**
 * The link that rebuilds this report. Absolute, because it is going into a
 * mail or onto a clipboard and a relative one is useless in both.
 */
export function reportUrl({ postcode, cert, valuationToken }) {
    if (typeof window === "undefined" || !postcode) return "";
    const params = new URLSearchParams({ postcode: postcode });
    if (cert && cert.certificateNumber) params.set("certificate", cert.certificateNumber);
    /*
     * The valuation travels too, when there is one.
     *
     * It could not before 31 October 2026 - the figure lived in one tab's
     * state, so a shared report arrived without the valuation its sender had
     * just paid a model to produce. It names a stored valuation rather than
     * carrying the number itself: a figure in a URL is a figure anyone can
     * edit before forwarding it.
     */
    if (valuationToken) params.set("valuation", valuationToken);
    return window.location.origin + window.location.pathname + "?" + params.toString();
}

/*
 * `brandName` is no longer taken: the server composes the whole message now,
 * so the brand in it comes from there rather than from whichever page
 * happened to render this button. The prop is still passed by
 * PropertyReport and simply ignored.
 */
export default function ShareReport({
    postcode,
    cert,
    disabled,
    valuationToken,
    open: openFromParent,
    onOpenChange,
}) {
    // "Copied" replaces the label for a moment, because a copy with no
    // feedback at all reads as a button that did nothing.
    const [copied, setCopied] = useState(false);

    /*
     * Open either on its own or at the parent's say-so. The valuation panel
     * at the foot of the report offers "Email it", and rather than build a
     * second form down there it opens this one - so the page can drive it
     * while a standalone use still works with no props at all.
     */
    const [openSelf, setOpenSelf] = useState(false);
    const open = openFromParent === undefined ? openSelf : openFromParent;
    const setOpen = (next) => {
        const value = typeof next === "function" ? next(open) : next;
        if (onOpenChange) onOpenChange(value);
        else setOpenSelf(value);
    };

    const [to, setTo] = useState("");
    const [sending, setSending] = useState(false);
    const [outcome, setOutcome] = useState(null);

    const where = (cert && cert.address) || postcode;
    const url = reportUrl({ postcode: postcode, cert: cert, valuationToken: valuationToken });

    /*
     * Send it from the server, with the PDF attached.
     *
     * THIS USED TO BE A mailto:. That opened the reader's own mail client
     * with a draft they then had to send - which is defensible, and which
     * nobody understood: reported on 31 October 2026 as "the mail didn't
     * arrive or send" by somebody looking at an unsent draft. It also could
     * not attach anything, because a mailto cannot.
     *
     * The body, the subject and the attachment are all composed on the
     * server; this posts one address and nothing else. See the long note
     * above /report/email in api/routes/propertyData.js for why that
     * distinction is the whole of the safety argument.
     */
    async function send(event) {
        event.preventDefault();
        if (sending) return;

        setSending(true);
        setOutcome(null);
        try {
            const res = await fetch(
                (process.env.REACT_APP_API_URL || "") + "property-data/report/email",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        postcode: postcode,
                        certificate: (cert && cert.certificateNumber) || undefined,
                        valuation: valuationToken || undefined,
                        to: to,
                    }),
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setOutcome({ bad: true, text: data.error || "Could not send it just now." });
                return;
            }
            setOutcome({
                bad: false,
                text: data.attached
                    ? "Sent to " + to + ", with the report attached."
                    : "Sent to " + to + ". The attachment could not be built, so it carries the link.",
            });
            setTo("");
        } catch (err) {
            /*
             * Says what happened rather than "Message Sent" regardless -
             * which is precisely the fault CLAUDE.md section 6g records
             * about the contact form, and not one to repeat here.
             */
            setOutcome({ bad: true, text: "Could not reach the server. Please try again." });
        } finally {
            setSending(false);
        }
    }

    async function share() {
        const payload = { title: "Property report — " + where, text: where, url: url };
        /*
         * The Web Share API is the native sheet on a phone, which is where
         * "share" actually means something. It is absent on most desktop
         * browsers and rejects with AbortError when the sheet is dismissed -
         * neither is a failure worth reporting, so both fall through to the
         * clipboard.
         */
        try {
            if (navigator.share) {
                await navigator.share(payload);
                return;
            }
        } catch (err) {
            if (err && err.name === "AbortError") return; // they changed their mind
        }
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // Clipboard refused (insecure context, or permission). Showing the
            // link is the fallback that always works.
            window.prompt("Copy this link", url);
        }
    }

    return (
        <Row>
            <Secondary
                type="button"
                onClick={() => {
                    setOpen((was) => !was);
                    setOutcome(null);
                }}
                disabled={disabled || !postcode}
                aria-expanded={open}
                title="Email this report, with a PDF attached"
            >
                Email
            </Secondary>
            <Secondary
                type="button"
                onClick={share}
                disabled={disabled || !postcode}
                title="Share the link to this report"
            >
                {copied ? "Link copied" : "Share"}
            </Secondary>

            {open && (
                <Panel as="form" onSubmit={send}>
                    {/* Visually hidden rather than display:none, which would
                        take it from screen readers as well - the placeholder
                        is not a label and is not announced reliably. There is
                        no .sr-only class in this project to lean on. */}
                    <label
                        htmlFor="share-report-to"
                        style={{
                            position: "absolute",
                            width: "1px",
                            height: "1px",
                            overflow: "hidden",
                            clip: "rect(0 0 0 0)",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Email address to send this report to
                    </label>
                    <input
                        id="share-report-to"
                        type="email"
                        required
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="name@example.com"
                        autoComplete="email"
                        disabled={sending}
                    />
                    <Secondary type="submit" disabled={sending || !to}>
                        {sending ? "Sending…" : "Send"}
                    </Secondary>

                    {outcome ? (
                        <Note $bad={outcome.bad} role="status">
                            {outcome.text}
                        </Note>
                    ) : (
                        <Note>
                            {valuationToken
                                ? "Sends the report as a PDF, including the valuation, plus a link."
                                : "Sends the report as a PDF, plus a link."}
                        </Note>
                    )}
                </Panel>
            )}
        </Row>
    );
}
